
import { useToast } from "@/components/ui/use-toast";
import { initialScores } from "@/data/level3/initialData";
import { UseLevel3TrainingReturn } from "./types";
import { calculatePointTotal, calculateGrandTotal } from "./scoreUtils";
import { useScoreHandlers } from "./useScoreHandlers";
import { useAutoSave } from "./useAutoSave";
import { useResultSaver } from "./useResultSaver";
import { BallTendency } from "./types";

export const useLevel3Training = (): UseLevel3TrainingReturn => {
  const { toast } = useToast();
  
  // Récupérer les gestionnaires de base pour les scores
  const {
    scores,
    setScores,
    ballTendencies,
    setBallTendencies,
    selectedTest,
    setSelectedTest,
    currentPoint,
    setCurrentPoint,
    currentAttempt,
    setCurrentAttempt,
    handleScoreChange: originalHandleScoreChange,
    cancelLastEntry,
    handleFinish: originalHandleFinish,
    handleSelectTest: originalHandleSelectTest,
    handleBackToTests: originalHandleBackToTests
  } = useScoreHandlers(initialScores, toast);

  // Hooks pour la sauvegarde automatique et l'enregistrement des résultats
  const { clearLocalStorage } = useAutoSave({
    selectedTest,
    scores,
    ballTendencies,
    currentPoint,
    currentAttempt,
    setScores,
    setSelectedTest,
    setCurrentPoint,
    setCurrentAttempt,
    setBallTendencies
  });
  
  const { saveIndividualScore, saveTestResults } = useResultSaver();
  
  // Calculer le total des points pour un point donné
  const getPointTotal = (point: string) => {
    // Fixed parameter order: point first, then scores
    return calculatePointTotal(point, scores);
  };

  // Calculer le total général des points
  const getGrandTotal = () => {
    if (!selectedTest) return 0;
    // Fixed parameter order: testId first, then scores
    return calculateGrandTotal(selectedTest, scores);
  };

  // Override handleScoreChange to save individual score entries to database
  const handleScoreChange = async (score: number, tendency: BallTendency = null) => {
    console.log('=== handleScoreChange called ===');
    console.log('selectedTest:', selectedTest);
    console.log('currentPoint:', currentPoint);
    console.log('currentAttempt:', currentAttempt);
    console.log('score:', score);
    console.log('tendency:', tendency);
    
    try {
      // Call the original function to update local state
      originalHandleScoreChange(score, tendency);
      
      // Save individual score to database (non-blocking)
      if (selectedTest && currentPoint && currentAttempt !== undefined) {
        console.log('=== Calling saveIndividualScore ===');
        saveIndividualScore(selectedTest, currentPoint, currentAttempt, score, tendency).catch(error => {
          console.warn('Failed to save individual score:', error);
          // Don't throw error to avoid blocking UI
        });
      } else {
        console.warn('Cannot save score - missing data:', {
          selectedTest,
          currentPoint,
          currentAttempt
        });
      }
    } catch (error) {
      console.error('Error in handleScoreChange:', error);
      // Continue with local state update even if save fails
      originalHandleScoreChange(score, tendency);
    }
  };

  // Override handleFinish to save results to database and clear localStorage
  const handleFinish = async () => {
    if (!selectedTest) return;
    
    try {
      // Calculate total points
      const totalPoints = getGrandTotal();
      
      // Save test results to database (non-blocking)
      const success = await saveTestResults(selectedTest, totalPoints).catch(error => {
        console.warn('Failed to save test results:', error);
        return false;
      });
      
      if (success) {
        // Nettoyer les données du localStorage
        clearLocalStorage();
      }
    } catch (error) {
      console.error('Error in handleFinish:', error);
    }
    
    // Call the original handleFinish to handle UI updates
    originalHandleFinish();
  };

  // Override handleSelectTest to persist test selection and refresh favorites
  const handleSelectTest = (testId: string) => {
    try {
      // Ne nettoyer que si on change de test (pas lors de la restauration)
      if (selectedTest && selectedTest !== testId) {
        clearLocalStorage();
      }
      
      // Appeler la fonction originale
      originalHandleSelectTest(testId);
    } catch (error) {
      console.error('Error in handleSelectTest:', error);
      // Still call original function
      originalHandleSelectTest(testId);
    }
  };
  
  // Override handleBackToTests to refresh favorites
  const handleBackToTests = () => {
    try {
      console.log('Navigating back to test selection from Level3Training');
      // Nettoyer les données du localStorage
      clearLocalStorage();
      
      // Appeler la fonction originale
      originalHandleBackToTests();
    } catch (error) {
      console.error('Error in handleBackToTests:', error);
      // Still call original function
      originalHandleBackToTests();
    }
  };

  return {
    scores,
    ballTendencies,
    selectedTest,
    currentPoint,
    currentAttempt,
    handleScoreChange,
    cancelLastEntry,
    handleFinish,
    handleSelectTest,
    handleBackToTests,
    getPointTotal,
    getGrandTotal
  };
};
